import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Clock, Eye, Tag } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { blogService, BlogPost as BlogPostType } from '../services/blogService';
import { smartFormatText } from '../utils/textFormatter';

const BlogPost: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [post, setPost] = useState<BlogPostType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadPost = async () => {
      if (!id) {
        setError('文章ID不存在');
        setLoading(false);
        return;
      }

      try {
        const blogPost = await blogService.getPostById(id);
        if (!blogPost) {
          setError('文章不存在');
        } else if (blogPost.status !== 'published') {
          setError('文章未发布');
        } else {
          setPost(blogPost);
          // 增加阅读量
          await blogService.incrementViews(id);
        }
      } catch (err) {
        setError('加载文章失败');
        console.error('加载文章失败:', err);
      } finally {
        setLoading(false);
      }
    };

    loadPost();
  }, [id]);

  const handleBack = () => {
    navigate('/about-developer');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#3A7D78] flex items-center justify-center">
        <div className="text-white text-lg">加载中...</div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-[#3A7D78] flex items-center justify-center">
        <div className="text-center">
          <div className="text-white text-lg mb-4">{error || '文章不存在'}</div>
          <button
            onClick={handleBack}
            className="text-[#F5B6C1] hover:text-white transition-colors"
          >
            返回博客列表
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#3A7D78] text-white">
      {/* 导航栏 */}
      <nav className="fixed top-0 left-0 right-0 bg-[#3A7D78]/95 backdrop-blur-sm z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <button 
              onClick={handleBack}
              className="flex items-center gap-2 text-white hover:text-[#F5B6C1] transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="font-bold">返回博客列表</span>
            </button>
          </div>
        </div>
      </nav>

      {/* 文章内容 */}
      <article className="pt-24 pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* 封面图片 */}
            {post.coverImage && (
              <div className="mb-12">
                <img
                  src={post.coverImage}
                  alt={post.title}
                  className="w-full h-64 md:h-96 object-cover rounded-md"
                />
              </div>
            )}

            {/* 文章内容 - 标题和正文在同一个白色卡片中 */}
            <div className="bg-white rounded-md p-8 md:p-12 shadow-lg border border-gray-200">
              {/* 文章头部 */}
              <header className="mb-6">
                <h1 className="text-4xl md:text-5xl font-bold text-[#1A5D58] mb-6 leading-tight">
                  {post.title}
                </h1>
                
                {/* 文章元信息 */}
                <div className="flex flex-wrap items-center gap-6 text-[#2A6D68]/70 mb-6">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    <span>{formatDate(post.publishedAt)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    <span>{post.readTime} 分钟阅读</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Eye className="h-4 w-4" />
                    <span>{post.views} 次阅读</span>
                  </div>
                </div>

                {/* 标签 */}
                {post.tags.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2">
                    <Tag className="h-4 w-4 text-[#1A5D58]" />
                    {post.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-[#1A5D58]/10 text-[#1A5D58] rounded-full text-sm border border-[#1A5D58]/20"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </header>
              
              {/* 分隔线 */}
              <div className="border-t border-gray-100 mb-4"></div>
              
              {/* 文章正文 */}
              <div 
                className="prose prose-base max-w-none"
                style={{
                  color: '#1A5D58',
                  lineHeight: '1.8',
                  fontWeight: '300'
                }}
                dangerouslySetInnerHTML={{ __html: smartFormatText(post.content) }}
              />
            </div>


          </motion.div>
        </div>
      </article>
    </div>
  );
};

export default BlogPost;